import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import BuyerDashboard from '@/pages/BuyerDashboard';
import SellerDashboard from '@/pages/SellerDashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import MineralDetail from '@/pages/MineralDetail';
import MessagesPage from '@/pages/MessagesPage';
import CreateListing from '@/pages/CreateListing';
import MarketInsights from '@/pages/MarketInsights';
import HowItWorks from '@/pages/HowItWorks';
import DocumentUpload from '@/pages/DocumentUpload';
import InspectionPage from '@/pages/InspectionPage';
import DealDetail from '@/pages/DealDetail';
import VerifyEmail from '@/pages/VerifyEmail';
import ResetPassword from '@/pages/ResetPassword';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import '@/App.css';
import '@/i18n';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Admin goes to admin dashboard, others go home
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/'} />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/auth" element={user ? <Navigate to={user.role === 'admin' ? '/admin/dashboard' : `/${user.role}`} /> : <AuthPage />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ResetPassword />} />
      <Route path="/mineral/:id" element={<MineralDetail />} />
      <Route path="/minerals/:id" element={<MineralDetail />} />
      
      <Route
        path="/buyer"
        element={
          <PrivateRoute allowedRoles={['buyer']}>
            <BuyerDashboard />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/seller"
        element={
          <PrivateRoute allowedRoles={['seller']}>
            <SellerDashboard />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/seller/create-listing"
        element={
          <PrivateRoute allowedRoles={['seller']}>
            <CreateListing />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/seller/market-insights"
        element={
          <PrivateRoute allowedRoles={['seller']}>
            <MarketInsights />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/seller/documents"
        element={
          <PrivateRoute allowedRoles={['seller']}>
            <DocumentUpload />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Navigate to="/admin/dashboard" replace />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/messages"
        element={
          <PrivateRoute>
            <MessagesPage />
          </PrivateRoute>
        }
      />
      
      <Route
        path="/inspection"
        element={
          <PrivateRoute allowedRoles={['buyer', 'seller']}>
            <InspectionPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/deals/:dealId"
        element={
          <PrivateRoute allowedRoles={['buyer', 'seller', 'admin']}>
            <DealDetail />
          </PrivateRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}